import React, { useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceArea, ReferenceLine, Legend, Label,
} from "recharts";
import ChartContainer from "./ChartContainer";
import { SERIES_COLORS, fmtNum } from "../lib/api";

const tooltipStyle = {
  backgroundColor: "#FCD34D",
  border: "1px solid #B45309",
  borderRadius: "10px",
  color: "#111827",
  fontFamily: "JetBrains Mono, monospace",
  fontSize: 12,
  boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
};
const tooltipLabelStyle = { color: "#7C2D12", fontWeight: 600 };
const tooltipItemStyle = { color: "#111827" };

// Hoisted constants so React doesn't allocate new objects on every render.
const CHART_MARGIN = { top: 8, right: 16, left: 0, bottom: 8 };
const AXIS_TICK_STYLE = { fill: "#94A3B8", fontSize: 11, fontFamily: "JetBrains Mono" };
const LEGEND_STYLE = { color: "#94A3B8", fontSize: 12 };
const DOT_STYLE = { r: 3, strokeWidth: 0 };
const ACTIVE_DOT_STYLE = { r: 6 };

const tooltipFormatter = (v) => (typeof v === "number" ? fmtNum(v, 2) : v);
const yTickFormatter = (v) => fmtNum(v, 1);

/**
 * data: array of points - each point is {date, [sailorName]: value, ...}
 * series: [{key, name, color, range?}]  -- if a series has its own range, dashed
 *         low/high lines will be drawn in the series' colour.
 * range: {low, high} -> draws the (single) reference area in safe band.
 *         Used when *all* series share the same parameter (e.g. compare-athletes).
 */
export default function TrendChart({ data, series, range, yLabel, height = 320, testid }) {
  // Compute the Y-axis domain BEFORE any early return so the useMemo
  // hooks run unconditionally on every render (React rules-of-hooks).
  const yDomain = useMemo(() => {
    if (!data || data.length === 0) return [0, 1];
    const allVals = [];
    data.forEach((p) => series.forEach((s) => {
      const v = p[s.key];
      if (typeof v === "number") allVals.push(v);
    }));
    if (range?.low != null) allVals.push(range.low);
    if (range?.high != null) allVals.push(range.high);
    series.forEach((s) => {
      if (s?.range?.low != null) allVals.push(s.range.low);
      if (s?.range?.high != null) allVals.push(s.range.high);
    });
    if (allVals.length === 0) return [0, 1];
    const min = Math.min(...allVals);
    const max = Math.max(...allVals);
    const padding = (max - min) * 0.18 || 1;
    return [min - padding, max + padding];
  }, [data, series, range]);
  const yLabelProp = useMemo(
    () => (yLabel ? { value: yLabel, angle: -90, position: "insideLeft", fill: "#64748B", fontSize: 11 } : undefined),
    [yLabel],
  );

  if (!data || data.length === 0) {
    return (
      <div className="h-64 grid place-items-center text-slate-500 text-sm">
        No data for this selection
      </div>
    );
  }

  return (
    <div data-testid={testid} style={{ width: "100%", height, minWidth: 0 }}>
      <ChartContainer>
        <LineChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1E304B" />
          <XAxis dataKey="date" axisLine={false} tickLine={false}
                 tick={AXIS_TICK_STYLE} />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={AXIS_TICK_STYLE}
            domain={yDomain}
            tickFormatter={yTickFormatter}
            label={yLabelProp}
          />
          <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={tooltipFormatter} />
          {(range?.low != null || range?.high != null) && (
            <ReferenceArea
              y1={range.low ?? "dataMin"}
              y2={range.high ?? "dataMax"}
              fill="#10B981"
              fillOpacity={0.08}
              stroke="transparent"
            />
          )}
          {range?.low != null && (
            <ReferenceLine
              y={range.low}
              stroke="#F59E0B"
              strokeWidth={1.5}
              strokeDasharray="5 4"
              ifOverflow="extendDomain"
            >
              <Label
                value={`Low ${fmtNum(range.low, 2)}`}
                position="insideBottomRight"
                fill="#FBBF24"
                fontSize={11}
                fontFamily="JetBrains Mono"
              />
            </ReferenceLine>
          )}
          {range?.high != null && (
            <ReferenceLine
              y={range.high}
              stroke="#EF4444"
              strokeWidth={1.5}
              strokeDasharray="5 4"
              ifOverflow="extendDomain"
            >
              <Label
                value={`High ${fmtNum(range.high, 2)}`}
                position="insideTopRight"
                fill="#FCA5A5"
                fontSize={11}
                fontFamily="JetBrains Mono"
              />
            </ReferenceLine>
          )}
          {series.length > 1 && (
            <Legend wrapperStyle={LEGEND_STYLE} iconType="circle" />
          )}
          {series.map((s, i) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name || s.key}
              stroke={s.color || SERIES_COLORS[i % SERIES_COLORS.length]}
              strokeWidth={2.5}
              dot={DOT_STYLE}
              activeDot={ACTIVE_DOT_STYLE}
              animationDuration={700}
              connectNulls
            />
          ))}
          {/* Per-series limit lines — drawn in the line's own colour so it's
              instantly obvious which Low/High belongs to which parameter.
              Only used in multi-param mode; in single-param mode the
              ReferenceLine above already shows the limits with bigger labels. */}
          {series.length > 1 && series.map((s, i) => {
            const c = s.color || SERIES_COLORS[i % SERIES_COLORS.length];
            return (
              <React.Fragment key={`lim-${s.key}`}>
                {s?.range?.low != null && (
                  <ReferenceLine
                    y={s.range.low}
                    stroke={c}
                    strokeWidth={1}
                    strokeDasharray="3 5"
                    strokeOpacity={0.7}
                    ifOverflow="extendDomain"
                  >
                    <Label
                      value={`${s.name || s.key} Low ${fmtNum(s.range.low, 2)}`}
                      position="insideBottomLeft"
                      fill={c}
                      fontSize={10}
                      fontFamily="JetBrains Mono"
                      fillOpacity={0.85}
                    />
                  </ReferenceLine>
                )}
                {s?.range?.high != null && (
                  <ReferenceLine
                    y={s.range.high}
                    stroke={c}
                    strokeWidth={1}
                    strokeDasharray="3 5"
                    strokeOpacity={0.7}
                    ifOverflow="extendDomain"
                  >
                    <Label
                      value={`${s.name || s.key} High ${fmtNum(s.range.high, 2)}`}
                      position="insideTopLeft"
                      fill={c}
                      fontSize={10}
                      fontFamily="JetBrains Mono"
                      fillOpacity={0.85}
                    />
                  </ReferenceLine>
                )}
              </React.Fragment>
            );
          })}
        </LineChart>
      </ChartContainer>
    </div>
  );
}
